import React from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";

/**
 * Reusable drag-and-drop field list for the report template editor.
 * items: [{ key|id, visible, label }] — label overrides the registry default.
 * registry: { key: defaultLabel }
 * renamable: show a rename input per row.
 * rowExtras: (item) => ReactNode — extra action buttons (e.g. edit custom block).
 */
const Row = ({ item, idx, count, registry, renamable, onToggle, onRename, onMove, rowExtras, testPrefix }) => {
  const id = item.key || item.id;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const defaultLabel = registry[id] || item.title || id;
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="flex items-center gap-2 rounded-md border border-theme bg-theme-surface px-2 py-1.5"
      data-testid={`${testPrefix}-row-${id}`}
    >
      <button type="button" className="cursor-grab text-muted-foreground touch-none" {...attributes} {...listeners} data-testid={`${testPrefix}-drag-${id}`}>
        <GripVertical className="w-4 h-4" />
      </button>
      <Checkbox
        checked={item.visible !== false}
        onCheckedChange={(v) => onToggle(id, !!v)}
        data-testid={`${testPrefix}-toggle-${id}`}
      />
      {renamable ? (
        <Input
          value={item.label || ""}
          onChange={(e) => onRename(id, e.target.value)}
          placeholder={defaultLabel}
          className="h-7 text-xs flex-1 input-glass"
          data-testid={`${testPrefix}-rename-${id}`}
        />
      ) : (
        <span className="text-sm text-foreground flex-1 truncate">{item.title || defaultLabel}</span>
      )}
      {rowExtras && rowExtras(item)}
      <div className="flex flex-col">
        <Button type="button" variant="ghost" size="icon" className="h-4 w-5" disabled={idx === 0}
          onClick={() => onMove(idx, idx - 1)} data-testid={`${testPrefix}-up-${id}`}>
          <ChevronUp className="w-3 h-3" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-4 w-5" disabled={idx === count - 1}
          onClick={() => onMove(idx, idx + 1)} data-testid={`${testPrefix}-down-${id}`}>
          <ChevronDown className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

export const SortableFieldList = ({ items, registry = {}, onChange, renamable = true, rowExtras, testPrefix = "field" }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const ids = items.map((i) => i.key || i.id);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onChange(arrayMove(items, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  const onMove = (from, to) => onChange(arrayMove(items, from, to));
  const onToggle = (id, visible) => onChange(items.map((i) => ((i.key || i.id) === id ? { ...i, visible } : i)));
  const onRename = (id, label) => onChange(items.map((i) => ((i.key || i.id) === id ? { ...i, label: label || null } : i)));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5">
          {items.map((item, idx) => (
            <Row key={item.key || item.id} item={item} idx={idx} count={items.length}
              registry={registry} renamable={renamable} onToggle={onToggle}
              onRename={onRename} onMove={onMove} rowExtras={rowExtras} testPrefix={testPrefix} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableFieldList;
